import {useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {CeldaEstado} from '@/components/ui/celdas-tabla'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs'
import {DataTable, type Column} from '@/components/data-table'
import {KairosMark} from '@/components/kairos-mark'
import {ListCard, ListEmptyState, StatCard} from '@/components/section-card'
import {NoAccessState} from '@/components/no-access-state'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {QuotaMatrixDisplay} from '@/components/partner-quota-matrix'
import {AutoDebitoSection} from '@/components/billing/auto-debito-section'
import {useAuth} from '@/hooks/platform/use-auth'
import {useViewAsPartner} from '@/hooks/platform/use-view-as-partner'
import {useTableState} from '@/hooks/common/use-table-state'
import {useIsMobile} from '@/hooks/common/use-media-query'
import {translateApiError} from '@/lib/errors'
import {formatCopFull} from '@/lib/money'
import {formatDate} from '@/lib/risk'
import {
  PARTNER_PANEL_PAGE_SIZE,
  useApprovePartnerBusiness,
  useIsPartner,
  usePartnerActivity,
  usePartnerBillingCycle,
  usePartnerInvoices,
  usePartnerMe,
  usePartnerPanelBusinesses,
  usePartnerPendingBusinesses,
  usePartnerQuota,
  usePartnerPaymentSourceConfig,
  usePutPartnerPaymentSource,
  useDeletePartnerPaymentSource,
  useRejectPartnerBusiness,
  useUpdatePartnerBillingCycle,
  type PartnerActivity,
  type PartnerBillingCycle,
  type PartnerBusinessRow,
  type PartnerInvoice,
} from '@/hooks/inci/aliados/use-partner-panel'
import {SearchInput} from '@/components/ui/search-input'

function bandTone(banda: string | null): 'green' | 'amber' | 'red' | 'neutral' {
  if (banda === 'aceptable') return 'green'
  if (banda === 'moderadamente_aceptable') return 'amber'
  if (banda === 'critico') return 'red'
  return 'neutral'
}

const INVOICE_TONE: Record<string, 'green' | 'amber' | 'red' | 'neutral'> = {
  pagada: 'green',
  emitida: 'amber',
  vencida: 'red',
  anulada: 'neutral',
}

const SUBSCRIPTION_TONE: Record<string, 'green' | 'amber' | 'red' | 'neutral'> = {
  activa: 'green',
  pendiente_pago: 'amber',
  en_gracia: 'amber',
  degradada: 'red',
  cancelada: 'neutral',
}

function nameColumn(t: (key: string) => string): Column<PartnerBusinessRow> {
  return {
    key: 'name',
    header: t('partnerPanel.businesses.name'),
    cell: (b) => (
      <div className="min-w-0">
        <span className="block truncate font-medium text-foreground">{b.name}</span>
        <span className="block truncate font-mono text-xs text-muted-foreground">{b.nit}</span>
      </div>
    ),
  }
}

function QuotaCard() {
  const {t} = useTranslation()
  const {data, isLoading} = usePartnerQuota()
  if (isLoading || !data) return <Skeleton className="h-24 w-full" />
  return (
    <div className="space-y-3" data-testid="partner-quota-card">
      <div className="grid gap-3 sm:grid-cols-2">
        <StatCard
          label={t('partnerPanel.quota.used')}
          value={
            <span data-testid="partner-quota-used">
              {data.usadas}
              <span className="text-muted-foreground"> / {data.quota}</span>
            </span>
          }
          pct={data.quota ? (data.usadas / data.quota) * 100 : undefined}
        />
        <StatCard
          label={t('partnerPanel.quota.available')}
          value={<span data-testid="partner-quota-available">{data.disponibles}</span>}
          detail={t('partnerPanel.quota.hint')}
        />
      </div>
    </div>
  )
}

function CupoSection() {
  const {data, isLoading} = usePartnerQuota()
  if (isLoading || !data) return <Skeleton className="h-40 w-full" />
  return <QuotaMatrixDisplay celdas={data.celdas} />
}

function PendingApprovalsSection() {
  const {t} = useTranslation()
  const table = useTableState('created_at')
  const {data, isLoading} = usePartnerPendingBusinesses({page: table.page})
  const approve = useApprovePartnerBusiness()
  const reject = useRejectPartnerBusiness()
  const [rejectTarget, setRejectTarget] = useState<PartnerBusinessRow | null>(null)
  const [error, setError] = useState<string | null>(null)
  // «Ver como partner» es de solo lectura: `require_partner_writable` responde 403 a la identidad prestada.
  const {target} = useViewAsPartner()
  const readOnly = !!target

  function confirmReject() {
    if (!rejectTarget) return
    reject.mutate(rejectTarget.business_id, {
      onSuccess: () => setRejectTarget(null),
      onError: (err) => setError(translateApiError(err)),
    })
  }

  const columns: Column<PartnerBusinessRow>[] = [
    nameColumn(t),
    {
      key: 'actions',
      header: '',
      width: 'w-56',
      /* «Aprobar» muta al primer clic y «Rechazar» confirma: la variante de rol las distingue en filas idénticas. */
      cell: (b) => (
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            variant="destructive"
            data-testid={`partner-pending-reject-${b.business_id}`}
            disabled={readOnly || reject.isPending}
            onClick={(e) => {
              e.stopPropagation()
              setError(null)
              setRejectTarget(b)
            }}>
            {t('partnerPanel.pendientesAprobacion.reject')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            data-testid={`partner-pending-approve-${b.business_id}`}
            disabled={readOnly || approve.isPending}
            onClick={(e) => {
              e.stopPropagation()
              setError(null)
              approve.mutate(b.business_id, {onError: (err) => setError(translateApiError(err))})
            }}>
            {t('partnerPanel.pendientesAprobacion.approve')}
          </Button>
        </div>
      ),
    },
  ]

  if (isLoading && !data) return <Skeleton className="h-32 w-full" />

  return (
    <div data-testid="partner-pending-approvals-section">
      <ListCard
        title={t('partnerPanel.pendientesAprobacion.title')}
        toolbar={
          error ? (
            <p className="text-sm text-destructive" data-testid="partner-pending-error">
              {error}
            </p>
          ) : undefined
        }
        footer={
          !!data?.total && (
            <PaginationBar
              page={table.page}
              pageSize={PARTNER_PANEL_PAGE_SIZE}
              total={data.total}
              onPageChange={table.setPage}
            />
          )
        }>
        {data && data.items.length === 0 ? (
          <ListEmptyState title={t('partnerPanel.pendientesAprobacion.empty')} />
        ) : (
          <DataTable columns={columns} rows={data?.items ?? []} rowKey={(b) => b.business_id} />
        )}
      </ListCard>
      <ConfirmDialog
        open={!!rejectTarget}
        onOpenChange={(open) => !open && setRejectTarget(null)}
        title={t('partnerPanel.pendientesAprobacion.rejectTitle')}
        description={t('partnerPanel.pendientesAprobacion.rejectDescription', {name: rejectTarget?.name ?? ''})}
        confirmLabel={t('partnerPanel.pendientesAprobacion.rejectConfirm')}
        cancelLabel={t('partnerPanel.pendientesAprobacion.rejectCancel')}
        onConfirm={confirmReject}
        pending={reject.isPending}
      />
    </div>
  )
}

function BusinessesSection() {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const table = useTableState('name')
  const {data, isLoading} = usePartnerPanelBusinesses({page: table.page, search: table.search})
  const enTelefono = useIsMobile()

  const columns: Column<PartnerBusinessRow>[] = [
    nameColumn(t),
    ...(enTelefono
      ? []
      : [
          {
            key: 'plan',
            header: t('partnerPanel.businesses.plan'),
            width: 'w-24',
            cell: (b: PartnerBusinessRow) => (
              <CeldaEstado chip={<Tag tone="neutral">{t(`partnerPanel.plan.${b.plan_tier}`)}</Tag>} />
            ),
          } satisfies Column<PartnerBusinessRow>,
        ]),
    {
      key: 'compliance',
      header: t('partnerPanel.businesses.compliance'),
      width: 'w-48',
      cell: (b) => (
        <CeldaEstado
          chip={b.banda ? <Tag tone={bandTone(b.banda)}>{t(`partnerPanel.band.${b.banda}`)}</Tag> : undefined}
          meta={
            <>
              {!b.banda && t('partnerPanel.businesses.noAssessment')}
              {!b.banda && b.pendientes > 0 && ' · '}
              {b.pendientes > 0 && (
                <span className="text-destructive">
                  {t('partnerPanel.businesses.pendientes', {count: b.pendientes})}
                </span>
              )}
            </>
          }
        />
      ),
    },
    {
      key: 'estado_pago',
      header: t('partnerPanel.businesses.estadoPago'),
      width: 'w-32',
      cell: (b) =>
        b.estado_pago ? (
          <CeldaEstado
            chip={
              <Tag tone={SUBSCRIPTION_TONE[b.estado_pago] ?? 'neutral'}>
                {t(`partnerPanel.pagos.estado.${b.estado_pago}`)}
              </Tag>
            }
          />
        ) : b.plan_tier === 'free' && !b.partner_estado ? (
          // Registrada bajo el código pero fuera de cupo en su segmento: nunca entró a pendiente.
          <CeldaEstado meta={<span data-testid="partner-row-sin-cupo">{t('partnerPanel.businesses.sinCupo')}</span>} />
        ) : (
          <CeldaEstado meta={t('partnerPanel.businesses.sinFacturar')} />
        ),
    },
  ]

  if (isLoading && !data) return <Skeleton className="h-48 w-full" />

  return (
    <ListCard
      testId="partner-businesses-section"
      title={t('partnerPanel.businesses.title')}
      toolbar={
        <SearchInput
          value={table.searchInput}
          onChange={table.setSearchInput}
          placeholder={t('partnerPanel.businesses.searchPlaceholder')}
        />
      }
      footer={
        !!data?.total && (
          <PaginationBar
            page={table.page}
            pageSize={PARTNER_PANEL_PAGE_SIZE}
            total={data.total}
            onPageChange={table.setPage}
          />
        )
      }>
      {data && data.items.length === 0 ? (
        <ListEmptyState
          title={table.search ? t('partnerPanel.businesses.noResults') : t('partnerPanel.businesses.empty')}
        />
      ) : (
        <DataTable
          columns={columns}
          rows={data?.items ?? []}
          rowKey={(b) => b.business_id}
          onRowClick={(b) => navigate(`/businesses/${b.business_id}`)}
        />
      )}
    </ListCard>
  )
}

function ActivitySection() {
  const {t} = useTranslation()
  const table = useTableState('created_at')
  const {data, isLoading} = usePartnerActivity({page: table.page})

  const columns: Column<PartnerActivity>[] = [
    {
      key: 'business',
      header: t('partnerPanel.activity.business'),
      cell: (a) => <span className="truncate text-sm text-foreground">{a.business_name}</span>,
    },
    {
      key: 'actor',
      header: t('partnerPanel.activity.actor'),
      width: 'w-40',
      cell: (a) => <span className="truncate text-sm text-foreground">{a.actor_nombre}</span>,
    },
    {
      key: 'tipo',
      header: t('partnerPanel.activity.event'),
      width: 'w-48',
      cell: (a) => <CeldaEstado chip={<Tag tone="neutral">{t(`partnerPanel.activity.tipo.${a.tipo}`)}</Tag>} />,
    },
    {
      key: 'when',
      header: t('partnerPanel.activity.when'),
      width: 'w-48',
      cell: (a) => (
        <span className="font-mono text-xs text-muted-foreground">
          {new Date(a.created_at).toLocaleString('es-CO')}
        </span>
      ),
    },
  ]

  if (isLoading && !data) return <Skeleton className="h-40 w-full" />

  return (
    <ListCard
      title={t('partnerPanel.activity.title')}
      footer={
        !!data?.total && (
          <PaginationBar
            page={table.page}
            pageSize={PARTNER_PANEL_PAGE_SIZE}
            total={data.total}
            onPageChange={table.setPage}
          />
        )
      }>
      {data && data.items.length === 0 ? (
        <ListEmptyState title={t('partnerPanel.activity.empty')} />
      ) : (
        <DataTable columns={columns} rows={data?.items ?? []} rowKey={(a) => a.id} />
      )}
    </ListCard>
  )
}

/** Solo rige suscripciones futuras; apagado bajo «ver como partner» porque el endpoint responde 403. */
function BillingCycleToggle({value, disabled}: {value: PartnerBillingCycle; disabled: boolean}) {
  const {t} = useTranslation()
  const update = useUpdatePartnerBillingCycle()
  const [error, setError] = useState<string | null>(null)
  return (
    <div className="space-y-1.5">
      <div className="inline-flex rounded-lg border border-border p-0.5" data-testid="partner-billing-cycle-toggle">
        {(['mensual', 'anual'] as const).map((cycle) => (
          <button
            key={cycle}
            type="button"
            disabled={disabled || update.isPending}
            data-testid={`partner-billing-cycle-${cycle}`}
            aria-pressed={value === cycle}
            onClick={() => {
              setError(null)
              update.mutate(cycle, {onError: (err) => setError(translateApiError(err))})
            }}
            className={
              value === cycle
                ? 'rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground'
                : 'rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground disabled:pointer-events-none disabled:opacity-40'
            }>
            {t(`partnerPanel.pagos.cycle.${cycle}`)}
          </button>
        ))}
      </div>
      {error && (
        <p className="text-xs text-destructive" data-testid="partner-billing-cycle-error">
          {error}
        </p>
      )}
    </div>
  )
}

/** Bajo «ver como partner» es de solo lectura: el PUT/DELETE responde 403 a una identidad prestada. */
function PartnerAutoDebito() {
  const {user} = useAuth()
  const {target} = useViewAsPartner()
  const {data: config} = usePartnerPaymentSourceConfig()
  const put = usePutPartnerPaymentSource()
  const remove = useDeletePartnerPaymentSource()

  if (!config) return null

  return (
    <AutoDebitoSection
      source={config.source}
      wompi={config.wompi}
      defaultEmail={user?.email ?? ''}
      canEdit={!target}
      putPending={put.isPending}
      deletePending={remove.isPending}
      onSave={(payload) => put.mutateAsync(payload)}
      onRemove={() => remove.mutateAsync()}
    />
  )
}

function PagosSection() {
  const {t} = useTranslation()
  const table = useTableState('created_at')
  const {data, isLoading} = usePartnerInvoices({page: table.page})
  const {target} = useViewAsPartner()
  const enTelefono = useIsMobile()

  const columns: Column<PartnerInvoice>[] = [
    {
      key: 'numero',
      header: t('partnerPanel.pagos.invoices.numero'),
      width: 'w-32',
      cell: (row) => <span className="font-mono text-sm font-medium text-foreground">{row.numero_factura}</span>,
    },
    {
      key: 'tenant',
      header: t('partnerPanel.pagos.invoices.tenant'),
      cell: (row) => <span className="truncate text-sm text-foreground">{row.business_name}</span>,
    },
    {
      key: 'estado',
      header: t('partnerPanel.pagos.invoices.estado'),
      width: 'w-28',
      cell: (row) => (
        <CeldaEstado
          chip={
            <Tag tone={INVOICE_TONE[row.estado] ?? 'neutral'}>
              {t(`partnerPanel.pagos.invoiceEstado.${row.estado}`)}
            </Tag>
          }
        />
      ),
    },
    ...(enTelefono
      ? []
      : [
          {
            key: 'fecha',
            header: t('partnerPanel.pagos.invoices.fecha'),
            width: 'w-32',
            cell: (row: PartnerInvoice) => (
              <span className="font-mono text-xs text-muted-foreground">{formatDate(row.created_at)}</span>
            ),
          } satisfies Column<PartnerInvoice>,
        ]),
    {
      key: 'total',
      header: t('partnerPanel.pagos.invoices.total'),
      width: 'w-32',
      className: 'text-right',
      cell: (row) => (
        <span className="font-mono text-sm text-foreground tabular-nums">{formatCopFull(row.total_cop)}</span>
      ),
    },
    {
      key: 'link',
      header: '',
      width: 'w-32',
      cell: (row) =>
        row.payment_url ? (
          <a
            href={row.payment_url}
            target="_blank"
            rel="noreferrer"
            data-testid="partner-invoice-pay-link"
            onClick={(e) => e.stopPropagation()}
            className="text-sm font-medium text-primary hover:underline">
            {t('partnerPanel.pagos.invoices.pay')}
          </a>
        ) : null,
    },
  ]

  return (
    <div className="space-y-3" data-testid="partner-pagos-section">
      <PartnerAutoDebito />
      {isLoading && !data ? (
        <Skeleton className="h-48 w-full" />
      ) : (
        <ListCard
          title={t('partnerPanel.pagos.title')}
          actions={
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{t('partnerPanel.pagos.cyclePreferido')}</span>
              <PartnerCycleToggleContainer disabled={!!target} />
            </div>
          }
          footer={
            !!data?.total && (
              <PaginationBar
                page={table.page}
                pageSize={PARTNER_PANEL_PAGE_SIZE}
                total={data.total}
                onPageChange={table.setPage}
              />
            )
          }>
          {data && data.items.length === 0 ? (
            <ListEmptyState title={t('partnerPanel.pagos.empty')} />
          ) : (
            <DataTable columns={columns} rows={data?.items ?? []} rowKey={(row) => row.id} />
          )}
        </ListCard>
      )}
    </div>
  )
}

function PartnerCycleToggleContainer({disabled}: {disabled: boolean}) {
  const {data} = usePartnerBillingCycle()
  if (!data) return <Skeleton className="h-8 w-32" />
  return <BillingCycleToggle value={data.billing_cycle_preferido} disabled={disabled} />
}

function ViewAsPartnerBanner() {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const {target, exit} = useViewAsPartner()
  if (!target) return null
  return (
    <div
      data-testid="view-as-partner-banner"
      className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border border-primary/30 bg-accent px-3 py-2">
      <span className="flex items-center gap-2 text-sm font-medium text-primary">
        <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full bg-primary" />
        {t('partnerPanel.viewAs.banner', {name: target.name})}
      </span>
      <button
        type="button"
        data-testid="view-as-partner-exit"
        onClick={() => {
          exit()
          navigate('/admin/partners')
        }}
        className="ml-auto rounded-md border border-primary/40 px-2 py-1 text-xs font-semibold text-primary hover:bg-primary/10">
        {t('partnerPanel.viewAs.exit')}
      </button>
    </div>
  )
}

function PanelHeader() {
  const {t} = useTranslation()
  const {data: me} = usePartnerMe()
  return (
    <div>
      <div className="flex items-center gap-2 text-foreground">
        <KairosMark />
        <h1 className="font-heading text-xl" data-testid="partner-panel-display-name">
          {me?.display_name ?? t('partnerPanel.title')}
        </h1>
      </div>
    </div>
  )
}

/** Componente propio para que su consulta cuelgue de la tab: `PartnerPanelPage` corta antes de sus hooks si no es partner. */
function PendingTabChip() {
  const {t} = useTranslation()
  const {data} = usePartnerPendingBusinesses({page: 1})
  if (!data?.total) return null
  return (
    <span
      className="ml-1.5 rounded-full bg-accent px-1.5 py-0.5 font-mono text-[10px] font-semibold text-primary"
      data-testid="partner-pending-badge">
      {t('partnerPanel.pendientesAprobacion.badge', {count: data.total})}
    </span>
  )
}

export function PartnerPanelPage() {
  const {t} = useTranslation()
  const isPartner = useIsPartner()
  const {target} = useViewAsPartner()
  if (!isPartner && !target) return <NoAccessState />
  return (
    <div className="space-y-6" data-testid="partner-panel-page">
      <ViewAsPartnerBanner />
      <PanelHeader />
      <QuotaCard />

      <Tabs defaultValue="empresas" data-testid="partner-panel-tabs">
        <TabsList>
          <TabsTrigger value="empresas" data-testid="partner-panel-tab-empresas">
            {t('partnerPanel.tabs.empresas')}
          </TabsTrigger>
          <TabsTrigger value="aprobaciones" data-testid="partner-panel-tab-aprobaciones">
            {t('partnerPanel.tabs.aprobaciones')}
            <PendingTabChip />
          </TabsTrigger>
          <TabsTrigger value="pagos" data-testid="partner-panel-tab-pagos">
            {t('partnerPanel.tabs.pagos')}
          </TabsTrigger>
          <TabsTrigger value="actividad" data-testid="partner-panel-tab-actividad">
            {t('partnerPanel.tabs.actividad')}
          </TabsTrigger>
          <TabsTrigger value="cupo" data-testid="partner-panel-tab-cupo">
            {t('partnerPanel.tabs.cupo')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="empresas" className="pt-4">
          <BusinessesSection />
        </TabsContent>
        <TabsContent value="aprobaciones" className="pt-4">
          <PendingApprovalsSection />
        </TabsContent>
        <TabsContent value="pagos" className="pt-4">
          <PagosSection />
        </TabsContent>
        <TabsContent value="actividad" className="pt-4">
          <ActivitySection />
        </TabsContent>
        <TabsContent value="cupo" className="pt-4">
          <CupoSection />
        </TabsContent>
      </Tabs>
    </div>
  )
}
